import type { AppState } from '@store/types'
import type { PropsWithChildren } from 'react'
import { useLayoutEffect, useMemo, useRef, useState } from 'react'
import { useSelector } from 'react-redux'

import { edgePaths } from './edgePath'
import { COMPACT_LAYOUT_UPDATED } from './events'
import { createCompactLayout } from './layout'
import css from './styles.module.css'
import type { LayoutNode, MeasuredLayout } from './types'

interface Props extends PropsWithChildren {
  maxDrop: number
  frozen?: boolean
}

// DOM measurement stays in this adapter. The tree and project data remain intact.
export const CompactLayout = ({ maxDrop, frozen = false, children }: Props) => {
  const treeRef = useRef<HTMLDivElement>(null)
  const [layout, setLayout] = useState<MeasuredLayout | null>(null)

  useLayoutEffect(() => {
    const tree = treeRef.current
    if (!tree || frozen) {
      return
    }
    let frame = 0
    let previousSignature = ''
    const observed = new Set<Element>()
    // The badge is absolutely positioned, so offsetHeight leaves it out.
    // Rects are zoomed; dividing by the card's scale gives layout pixels.
    const readBelow = (card: HTMLElement) => {
      const badge = card.querySelector('[data-collapsed-badge]')
      if (!badge || !card.offsetHeight) {
        return 0
      }
      const cardRect = card.getBoundingClientRect()
      const scale = cardRect.height / card.offsetHeight
      if (!scale) {
        return 0
      }
      const below =
        (badge.getBoundingClientRect().bottom - cardRect.bottom) / scale
      return Math.max(0, Math.ceil(below))
    }
    const readBranch = (branch: Element): LayoutNode | null => {
      const card = branch.querySelector<HTMLElement>(':scope > .Tree-node')
      if (!card) {
        return null
      }
      const descendants = branch.querySelector(':scope > .Tree-children')
      const children = descendants
        ? Array.from(descendants.children)
            .filter((p) => p.classList.contains('Tree-branch'))
            .map(readBranch)
            .filter((p): p is LayoutNode => p !== null)
        : []
      return {
        id: card.id,
        width: card.offsetWidth,
        height: card.offsetHeight,
        below: readBelow(card),
        children,
      }
    }
    const measure = () => {
      const cards = Array.from(tree.querySelectorAll<HTMLElement>('.Tree-node'))
      for (const card of cards) {
        if (!observed.has(card)) {
          observer.observe(card)
          observed.add(card)
        }
      }
      for (const card of observed) {
        if (!tree.contains(card)) {
          observer.unobserve(card)
          observed.delete(card)
        }
      }
      const branch = tree.querySelector('.Tree-branch')
      const root = branch ? readBranch(branch) : null
      if (!root) {
        setLayout(null)
        return
      }
      if (cards.some((p) => p.offsetWidth === 0)) {
        return
      }
      const cardsById = new Map(cards.map((card) => [card.id, card]))
      const signature = JSON.stringify([
        root,
        cards.map((card) =>
          card.parentElement?.style.getPropertyValue('--tree-color')
        ),
      ])
      if (
        signature === previousSignature &&
        cards.every((card) => card.style.getPropertyValue('--compact-x'))
      ) {
        return
      }
      previousSignature = signature
      const result = createCompactLayout(root, maxDrop)
      for (const node of result.nodes) {
        const card = cardsById.get(node.id)
        card?.style.setProperty('--compact-x', `${node.x}px`)
        card?.style.setProperty('--compact-y', `${node.y}px`)
        const zones = card?.parentElement?.querySelectorAll<HTMLElement>(
          ':scope > [data-compact-dropzone]'
        )
        zones?.forEach((zone) => {
          const x =
            zone.dataset.compactDropzone === 'before'
              ? node.x - 24
              : node.x + node.width
          zone.style.setProperty('--compact-drop-x', `${x}px`)
          zone.style.setProperty('--compact-drop-y', `${node.y}px`)
          zone.style.setProperty(
            '--compact-drop-height',
            `${Math.min(node.height, 80)}px`
          )
        })
      }
      const edges = result.edges.map((edge) => ({
        ...edge,
        color:
          cardsById
            .get(edge.to)
            ?.parentElement?.style.getPropertyValue('--tree-color') ||
          '#3185ff',
      }))
      setLayout({ ...result, edges })
    }
    const schedule = () => {
      cancelAnimationFrame(frame)
      frame = requestAnimationFrame(measure)
    }
    const observer = new ResizeObserver(schedule)
    const mutations = new MutationObserver((records) => {
      if (
        records.some(
          (record) =>
            record.type !== 'attributes' ||
            (record.target instanceof Element &&
              record.target.classList.contains('Tree-branch'))
        )
      ) {
        schedule()
      }
    })
    mutations.observe(tree, {
      childList: true,
      subtree: true,
      characterData: true,
      attributes: true,
      attributeFilter: ['style'],
    })
    measure()
    return () => {
      cancelAnimationFrame(frame)
      observer.disconnect()
      mutations.disconnect()
    }
  }, [maxDrop, frozen])

  useLayoutEffect(() => {
    if (layout) {
      window.dispatchEvent(new Event(COMPACT_LAYOUT_UPDATED))
    }
  }, [layout])

  const paths = useMemo(() => (layout ? edgePaths(layout.edges) : []), [layout])

  return (
    <div
      className={css.root}
      style={layout ? { width: layout.width, height: layout.height } : {}}
    >
      {layout && (
        <svg
          className={css.lines}
          width={layout.width}
          height={layout.height}
          aria-hidden="true"
        >
          {layout.edges.map((edge, i) => (
            <path
              stroke={edge.color}
              key={`${edge.from}-${edge.to}`}
              d={paths[i]}
            />
          ))}
        </svg>
      )}
      <div className={css.tree} ref={treeRef}>
        {children}
      </div>
    </div>
  )
}

interface BranchProps extends PropsWithChildren {
  enabled: boolean
}

// Wraps a root branch while the project is in Compact; otherwise the tree
// renders as it always has.
export const CompactBranch = ({ enabled, children }: BranchProps) => {
  const frozen = useSelector(
    (state: AppState) => state.artboard.dragAndDrop.isStarted
  )
  return enabled ? (
    <CompactLayout maxDrop={384} frozen={frozen}>
      {children}
    </CompactLayout>
  ) : (
    children
  )
}
